/**
 * LeadPreferences — buyer profile form for the CRM (PR #9 V1).
 *
 * All fields are optional and write through PATCH /leads/{id}.
 * Backend emits `lead.preferences_updated` when any preference field changes.
 *
 * With `includeChannels` the card also embeds the preferred-channel
 * selector (LeadChannelRows, preferred only — "Canal actual" is
 * operational state, not a buyer preference) under a "Comunicación"
 * group. Channel clicks patch immediately (their historical behaviour);
 * the preference fields keep the explicit "Guardar" submit.
 *
 * `embedded` drops the dv-card chrome so the parent can compose this
 * inside a larger surface (Lead Detail's "Información del comprador").
 * `showLanguage=false` removes the Idioma selector AND excludes language
 * from the submit payload — for surfaces where Contact already owns it.
 * `title={null}` hides the inner heading. Defaults keep LeadSidePanel
 * and any other consumer unchanged.
 */
import React, { useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { LeadChannelRows } from "@/components/leads/LeadChannelsCard";

const VILLA_MODELS = [
  { v: "dolce_vistas", l: "Dolce Vistas" },
  { v: "dolce_prive",  l: "Dolce Privè" },
  { v: "either",       l: "Cualquiera" },
];
const OBJECTIVES = [
  { v: "investment",   l: "Inversión" },
  { v: "personal_use", l: "Uso personal" },
  { v: "both",         l: "Ambos" },
];
const LANGUAGES = [
  { v: "es", l: "Español" },
  { v: "en", l: "Inglés" },
];
const TIMELINES = [
  { v: "lt_1m",   l: "< 1 mes" },
  { v: "1_3m",    l: "1-3 meses" },
  { v: "3_6m",    l: "3-6 meses" },
  { v: "6_12m",   l: "6-12 meses" },
  { v: "gt_12m",  l: "> 12 meses" },
  { v: "unknown", l: "Desconocido" },
];
const URGENCIES = [
  { v: "high",   l: "Alta" },
  { v: "medium", l: "Media" },
  { v: "low",    l: "Baja" },
];
const TEMPERATURES = [
  { v: "hot",  l: "Hot",  tone: "bg-red-50 border-red-200 text-red-700" },
  { v: "warm", l: "Warm", tone: "bg-amber-50 border-amber-200 text-amber-800" },
  { v: "cold", l: "Cold", tone: "bg-sky-50 border-sky-200 text-sky-700" },
];
const MOTIVATIONS = [
  { v: "roi",             l: "ROI" },
  { v: "lifestyle",       l: "Lifestyle" },
  { v: "diversification", l: "Diversificación" },
  { v: "relocation",      l: "Relocalización" },
  { v: "holiday_home",    l: "Segunda residencia" },
];

const FIELDS = [
  "budget_eur","villa_model_preference","purchase_objective","language",
  "buying_timeline","urgency","available_capital_eur","financing_needed",
  "motivation","lead_temperature",
];

export default function LeadPreferences({
  lead, onChanged, includeChannels = false, showLanguage = true,
  embedded = false, title = "Preferencias del comprador",
}) {
  const initial = useMemo(() => {
    const out = {};
    for (const k of FIELDS) out[k] = lead?.[k] ?? (k === "motivation" ? [] : "");
    return out;
  }, [lead]);
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  // Re-hydrate the form only when the persisted VALUES change — not on
  // every lead object identity change. An embedded channel click reloads
  // the lead (new object, same preference values); resetting on identity
  // would wipe an in-flight unsaved draft mid-form.
  const initialKey = JSON.stringify(initial);
  React.useEffect(() => {
    setForm(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey]);

  const toggleMotivation = (v) => {
    setForm((f) => {
      const cur = Array.isArray(f.motivation) ? f.motivation : [];
      return { ...f, motivation: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = {};
      for (const k of FIELDS) {
        // Contact owns language on surfaces that hide the selector here —
        // never write it back from a form that doesn't display it.
        if (k === "language" && !showLanguage) continue;
        const v = form[k];
        if (v === "" || v === undefined) continue;
        if (k === "motivation" && Array.isArray(v) && v.length === 0) continue;
        if ((k === "budget_eur" || k === "available_capital_eur") && v !== null && v !== "") {
          const n = Number(v);
          if (Number.isFinite(n)) payload[k] = n;
        } else {
          payload[k] = v;
        }
      }
      if (Object.keys(payload).length === 0) {
        setError("No hay cambios.");
        return;
      }
      await api.patch(`/leads/${lead.lead_id}`, payload);
      setSavedAt(new Date());
      onChanged && onChanged();
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  };

  // Empty-state check — true when no preference field is set on the lead.
  // The form still renders so admin/marketing can add preferences right
  // away; the empty hint only suppresses confusion when the card is blank.
  const hasAnyPreference = FIELDS.some((k) => {
    const v = lead?.[k];
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "string") return v.trim() !== "";
    return true;
  });

  return (
    <form
      onSubmit={submit}
      className={embedded ? "space-y-3" : "dv-card space-y-3"}
      data-testid="lead-preferences-card"
    >
      {(title || savedAt) && (
        <div className="flex items-center justify-between">
          {title ? <h3 className="text-sm font-medium text-stone-800">{title}</h3> : <span />}
          {savedAt && <span className="text-[11px] text-emerald-700">Guardado ✓</span>}
        </div>
      )}
      {!hasAnyPreference && (
        <div className="text-xs text-stone-500 italic" data-testid="lead-preferences-empty">
          Sin preferencias registradas todavía.
        </div>
      )}
      {error && (
        <div className="text-xs px-2 py-1.5 rounded border bg-red-50 border-red-200 text-red-700">{error}</div>
      )}

      {/* Interés — qué quiere comprar y con qué capacidad. */}
      <GroupLabel first>Interés</GroupLabel>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs text-stone-500">
          Presupuesto (EUR)
          <input
            type="number" min="0" step="1000" className="dv-input mt-1 w-full text-sm"
            value={form.budget_eur ?? ""}
            onChange={(e) => setForm({ ...form, budget_eur: e.target.value })}
            data-testid="lead-pref-budget"
          />
        </label>
        <label className="text-xs text-stone-500">
          Capital disponible (EUR)
          <input
            type="number" min="0" step="1000" className="dv-input mt-1 w-full text-sm"
            value={form.available_capital_eur ?? ""}
            onChange={(e) => setForm({ ...form, available_capital_eur: e.target.value })}
            data-testid="lead-pref-capital"
          />
        </label>
        <SimpleSelect label="Modelo preferido" value={form.villa_model_preference}
          options={VILLA_MODELS}
          onChange={(v) => setForm({ ...form, villa_model_preference: v })}
          testid="lead-pref-model" />
        <SimpleSelect label="Objetivo de compra" value={form.purchase_objective}
          options={OBJECTIVES}
          onChange={(v) => setForm({ ...form, purchase_objective: v })}
          testid="lead-pref-objective" />
        <label className="text-xs text-stone-500 flex items-end gap-2">
          <input
            type="checkbox" className="mb-1"
            checked={!!form.financing_needed}
            onChange={(e) => setForm({ ...form, financing_needed: e.target.checked })}
            data-testid="lead-pref-financing"
          />
          Necesita financiación
        </label>
      </div>

      {/* Decisión — cuándo y por qué compraría. */}
      <GroupLabel>Decisión</GroupLabel>
      <div className="grid sm:grid-cols-2 gap-3">
        <SimpleSelect label="Urgencia" value={form.urgency}
          options={URGENCIES}
          onChange={(v) => setForm({ ...form, urgency: v })}
          testid="lead-pref-urgency" />
        <SimpleSelect label="Horizonte de compra" value={form.buying_timeline}
          options={TIMELINES}
          onChange={(v) => setForm({ ...form, buying_timeline: v })}
          testid="lead-pref-timeline" />
      </div>

      <div>
        <div className="text-xs text-stone-500 mb-1">Motivación</div>
        <div className="flex flex-wrap gap-1.5">
          {MOTIVATIONS.map((m) => {
            const active = (form.motivation || []).includes(m.v);
            return (
              <button
                key={m.v}
                type="button"
                onClick={() => toggleMotivation(m.v)}
                data-testid={`lead-pref-motivation-${m.v}`}
                className={`px-2 py-1 rounded border text-xs ${
                  active
                    ? "bg-stone-900 text-white border-stone-900"
                    : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"
                }`}
              >
                {m.l}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="text-xs text-stone-500 mb-1">Temperatura del lead</div>
        <div className="flex gap-1.5">
          {TEMPERATURES.map((t) => {
            const active = form.lead_temperature === t.v;
            return (
              <button
                key={t.v}
                type="button"
                onClick={() => setForm({ ...form, lead_temperature: active ? "" : t.v })}
                data-testid={`lead-pref-temp-${t.v}`}
                className={`px-2 py-1 rounded border text-xs ${active ? t.tone : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"}`}
              >
                {t.l}
              </button>
            );
          })}
        </div>
      </div>

      {/* Comunicación — canal preferido (y el idioma sólo donde Contacto
          no lo posea ya). El canal patchea al instante (comportamiento
          heredado de LeadChannelsCard); no pasa por el submit. */}
      {(showLanguage || includeChannels) && <GroupLabel>Comunicación</GroupLabel>}
      {showLanguage && (
        <div className="grid sm:grid-cols-2 gap-3">
          <SimpleSelect label="Idioma" value={form.language}
            options={LANGUAGES}
            onChange={(v) => setForm({ ...form, language: v })}
            testid="lead-pref-language" />
        </div>
      )}
      {includeChannels && (
        <div className="space-y-3" data-testid="lead-profile-channels">
          <LeadChannelRows lead={lead} onChanged={onChanged} showCurrent={false} />
        </div>
      )}

      <button
        type="submit"
        disabled={busy}
        className="dv-btn dv-btn-primary text-sm w-full"
        data-testid="lead-pref-submit"
      >
        {busy ? "Guardando…" : "Guardar preferencias"}
      </button>
    </form>
  );
}

function GroupLabel({ children, first = false }) {
  return (
    <div
      className={`text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500 ${
        first ? "" : "pt-2 border-t border-stone-100 dark:border-stone-800"
      }`}
    >
      {children}
    </div>
  );
}

function SimpleSelect({ label, value, options, onChange, testid }) {
  return (
    <label className="text-xs text-stone-500">
      {label}
      <select
        className="dv-input mt-1 w-full text-sm"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.v} value={o.v}>{o.l}</option>
        ))}
      </select>
    </label>
  );
}
